// The author reading the piece aloud (design 4a, the "AUDIO" card under the
// byline): the entry point and the summary. The composer renders it always: a
// quiet dashed invitation until there's a recording, then the card.
//
// Recording, listening and syncing happen in the composer's dock at the
// bottom of the page (pages/composer/AudioDock), so the text stays readable
// and scrollable while the author reads it aloud: the card's Record,
// Re-record, Listen and Sync open the dock. Upload file and Remove work here.

import { useMemo, useRef, useState, type ReactNode } from 'react'
import type { Block, Narration } from '../../lib/types'
import { fileNarration, isNarrated, isPieceNarration, narrationTiming } from '../../lib/narration'
import { formatDuration, NARRATION_MAX_SECONDS, type CapturedAudio } from '../../lib/audio'
import { shortDate } from '../../lib/text'
import { InlineConfirm, smallBtn } from './InlineConfirm'
import { MicGlyph, PlayGlyph } from './icons'
import { useAudioFileUpload } from './useAudioFileUpload'

export interface NarrationCardProps {
  narration: Narration | null
  /** "Niki" in "Niki reads this writing". */
  authorName: string
  /** The writing's blocks, for "N of M paragraphs marked". */
  blocks?: Block[]
  /** Set, replace (upload) or remove the narration. */
  onChange(next: Narration | null): void
  /** Record / Re-record: the dock in recording mode. */
  onRecord?(): void
  /** Listen (the play button): the dock in listen mode, playing. */
  onListen?(): void
  /** Sync (single files): the dock in listen mode, syncing. */
  onSync?(): void
  /** The dock is open: the card waits (its actions are there). */
  docked?: boolean
  /** What the waiting card says (default "Open in the dock below"). */
  dockNote?: string
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function NarrationCard({
  narration,
  authorName,
  blocks = [],
  onChange,
  onRecord,
  onListen,
  onSync,
  docked = false,
  dockNote = 'Open in the dock below',
}: NarrationCardProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const file = useAudioFileUpload(NARRATION_MAX_SECONDS)
  /** An uploaded replacement waiting on "keep your marks?". */
  const [pending, setPending] = useState<CapturedAudio | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const narratedCount = useMemo(() => blocks.filter(isNarrated).length, [blocks])
  const markedCount = useMemo(() => {
    if (!narration) return 0
    const marks = narrationTiming(narration).marks
    return blocks.filter((b) => isNarrated(b) && typeof marks[b.id] === 'number').length
  }, [narration, blocks])
  const pieces = isPieceNarration(narration) ? narration.clips.length : 0

  const onPicked = async (f: File | undefined) => {
    setConfirmRemove(false)
    const a = await file.upload(f)
    if (!a) return
    if (narration && !pieces && markedCount > 0) setPending(a)
    else onChange(fileNarration(a))
  }

  const picker = (
    <input
      ref={fileInput}
      type="file"
      accept="audio/*"
      aria-label="Narration file"
      hidden
      onChange={(e) => {
        void onPicked(e.target.files?.[0])
        e.target.value = ''
      }}
    />
  )
  const uploadButton = (
    <button type="button" onClick={() => fileInput.current?.click()} disabled={!!file.busy || docked} className={smallBtn.outline}>
      {file.busy ?? 'Upload file'}
    </button>
  )
  const fileError = file.error && (
    <p role="alert" className="m-0 mt-2 basis-full text-[11.5px] font-medium text-primary">
      {file.error}
    </p>
  )

  if (!narration) {
    return (
      <section
        aria-label="Narration"
        data-testid="narration-card"
        data-state="empty"
        className="mb-[34px] flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-2xl border-[1.5px] border-dashed border-accent/25 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--accent)_4%,var(--card)),var(--card))] px-4 py-3.5 font-sans"
      >
        <span aria-hidden className="flex size-[34px] shrink-0 items-center justify-center rounded-full border border-accent/25 text-accent">
          <MicGlyph size={16} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="text-[14px] font-medium text-foreground">Read it aloud</span>
          <span className="text-[11.5px] font-medium text-muted-foreground">
            {docked ? dockNote : 'Record yourself reading this writing, a paragraph at a time'}
          </span>
        </div>
        {!docked && (
          <div className="flex gap-1 max-sm:w-full max-sm:pl-[48px]">
            <button type="button" onClick={onRecord} className={smallBtn.outline}>
              Record
            </button>
            {uploadButton}
          </div>
        )}
        {picker}
        {fileError}
      </section>
    )
  }

  const meta: ReactNode = [
    pieces ? plural(pieces, 'piece') : null,
    formatDuration(narration.duration),
    narration.recorded_at ? `recorded ${shortDate(narration.recorded_at)}` : null,
    pieces ? null : markedCount > 0 ? `${markedCount} of ${narratedCount} paragraphs marked` : 'syncs to paragraphs',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <section
      aria-label="Narration"
      data-testid="narration-card"
      data-state={docked ? 'docked' : 'ready'}
      data-kind={pieces ? 'pieces' : 'file'}
      className={`relative mb-[34px] rounded-2xl border border-card-edge bg-card px-4 py-3.5 font-sans transition-shadow ${
        docked ? 'shadow-[0_0_0_1.5px_var(--accent)]' : ''
      }`}
    >
      <span
        aria-hidden
        className="absolute -top-2.5 left-4 rounded-md bg-[color-mix(in_srgb,var(--accent)_18%,var(--card))] px-[7px] py-0.5 text-[9.5px] font-semibold tracking-[0.08em] text-accent"
      >
        AUDIO
      </span>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <button
          type="button"
          onClick={onListen}
          disabled={docked}
          aria-label="Listen"
          title="Listen, with the page following along"
          className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <PlayGlyph />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="text-[14px] font-medium text-foreground">{authorName} reads this writing</span>
          <span aria-live="polite" className="text-[11.5px] font-medium text-muted-foreground">
            {docked ? <span className="text-primary">{dockNote}</span> : meta}
          </span>
        </div>
        {!docked && (
          <div className="flex flex-wrap items-center gap-1 max-sm:w-full">
            {!pieces && (
              <button
                type="button"
                onClick={onSync}
                disabled={narratedCount === 0 || !(narration.duration > 0)}
                title="Mark where each paragraph starts, so readers can follow along"
                className={smallBtn.outline}
              >
                Sync
              </button>
            )}
            <button type="button" onClick={onRecord} className={smallBtn.outline}>
              Re-record
            </button>
            {uploadButton}
            <button
              type="button"
              onClick={() => {
                setPending(null)
                setConfirmRemove(true)
              }}
              className={smallBtn.ghost}
            >
              Remove
            </button>
          </div>
        )}
      </div>
      {picker}
      {fileError}

      {pending && (
        <InlineConfirm
          className="mt-3"
          message={`Keep your ${plural(markedCount, 'paragraph mark')}?`}
          detail="They were timed to the old recording."
          confirmLabel="Keep marks"
          cancelLabel="Start fresh"
          onConfirm={() => {
            onChange(fileNarration(pending, narration))
            setPending(null)
          }}
          onCancel={() => {
            onChange(fileNarration(pending))
            setPending(null)
          }}
          // Escape is the cautious answer: keep them (they can be cleared later).
          onDismiss={() => {
            onChange(fileNarration(pending, narration))
            setPending(null)
          }}
        />
      )}
      {confirmRemove && (
        <InlineConfirm
          className="mt-3"
          message="Remove the narration?"
          detail="Readers won’t be able to listen. It stays in History."
          confirmLabel="Remove"
          focus="cancel"
          onConfirm={() => {
            setConfirmRemove(false)
            onChange(null)
          }}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
    </section>
  )
}
